const initializedPixelIds = new Set<string>();
let pageViewTracked = false;
// Public identifier already used by byronda.com. The environment variable can
// override it for staging, while local and desktop builds remain disabled.
const PRODUCTION_PIXEL_ID = "1979719546061052";

type MetaPixel = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: (...args: unknown[]) => void;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: MetaPixel;
    _fbq?: MetaPixel;
  }
}

function configuredPixelId(): string | null {
  const id = import.meta.env.VITE_META_PIXEL_ID?.trim();
  if (id) return id;
  if (typeof window === "undefined") return null;
  return window.location?.hostname === "app.byronda.com"
    ? PRODUCTION_PIXEL_ID
    : null;
}

/**
 * Loads Meta only when Ronda has a real advertising conversion to report.
 *
 * The authenticated product contains clinical routes. Initializing the pixel
 * at application boot would expose every visited path as a PageView, even
 * though advertising only needs the completion of the free-trial signup.
 */
function pixel(): MetaPixel | null {
  const pixelId = configuredPixelId();
  if (!pixelId || typeof window === "undefined") return null;

  if (!window.fbq) {
    const fbq: MetaPixel = (...args: unknown[]) => {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    };
    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    window._fbq = fbq;
  }

  if (!initializedPixelIds.has(pixelId)) {
    window.fbq("init", pixelId);
    initializedPixelIds.add(pixelId);
  }

  if (
    typeof document !== "undefined" &&
    !document.querySelector("script[data-ronda-meta-pixel]")
  ) {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.dataset.rondaMetaPixel = "true";
    document.head.appendChild(script);
  }

  return window.fbq;
}

/** Sends the base PageView once on Ronda's acquisition and signup screens. */
export function trackMetaPageViewOnce(): boolean {
  if (pageViewTracked) return false;
  const fbq = pixel();
  if (!fbq) return false;
  fbq("track", "PageView");
  pageViewTracked = true;
  return true;
}

/** Reports one StartTrial conversion for each organization created here. */
export function trackMetaStartTrialOnce(
  organizationId: string,
  country: string,
): boolean {
  const storageKey = `ronda:meta:start-trial:${organizationId}`;
  try {
    if (localStorage.getItem(storageKey)) return false;
  } catch {
    // Browsers can disable storage; the conversion can still be sent.
  }

  const fbq = pixel();
  if (!fbq) return false;

  // No names, emails, phone numbers or clinical data are sent to Meta.
  fbq("track", "StartTrial", { country });
  try {
    localStorage.setItem(storageKey, "1");
  } catch {
    // Sending the conversion must not block a successful signup.
  }
  return true;
}
